import { unreachable } from '../../../ts-utils';
import { getCurrentOwnerId } from '../members/members-selectors';
// The result of this function used both in components to check if elements should be shown/hidden
// and controller thunks - to lift user permissions on action.
export const getAppPermissionsState = ({ isLocked, isRepliesDisabled, permissions, currentOwner, isPreconditionRequiredForAction, isWriteBeforeLoginDisabled, srcResourceId, }) => {
    const memberType = resolveMemberType(currentOwner);
    return {
        CREATE_COMMENT: (() => {
            if (isLocked) {
                return { type: 'DENY', reason: 'LOCKED' };
            }
            if (permissions.permissions.createComment) {
                return {
                    type: 'ALLOW',
                    precondition: memberType === 'VISITOR' ? 'GUEST_FLOW' : 'NONE',
                };
            }
            return predictActionPermission({
                memberType,
                permissions,
                isConsumerPreconditionRequired: isPreconditionRequiredForAction === null || isPreconditionRequiredForAction === void 0 ? void 0 : isPreconditionRequiredForAction('CREATE_COMMENT', {
                    srcResourceId,
                    memberType,
                }),
                isVisitorToMemberDisabled: isWriteBeforeLoginDisabled,
            });
        })(),
        UPDATE: (comment) => {
            var _a;
            if (permissions.permissions.updateAnyComment) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            if (((_a = comment.author) === null || _a === void 0 ? void 0 : _a.identityId) === getCurrentOwnerId(currentOwner)) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            return { type: 'DENY', reason: 'UNKNOWN' };
        },
        DELETE: (comment) => {
            var _a;
            if (permissions.permissions.deleteAnyComment) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            if (((_a = comment.author) === null || _a === void 0 ? void 0 : _a.identityId) === getCurrentOwnerId(currentOwner)) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            return { type: 'DENY', reason: 'UNKNOWN' };
        },
        CREATE_REPLY: (() => {
            if (isLocked || isRepliesDisabled) {
                return { type: 'DENY', reason: 'LOCKED' };
            }
            if (permissions.permissions.createReply) {
                return {
                    type: 'ALLOW',
                    precondition: memberType === 'VISITOR' ? 'GUEST_FLOW' : 'NONE',
                };
            }
            return predictActionPermission({
                memberType,
                permissions,
                isConsumerPreconditionRequired: isPreconditionRequiredForAction === null || isPreconditionRequiredForAction === void 0 ? void 0 : isPreconditionRequiredForAction('CREATE_REPLY', {
                    srcResourceId,
                    memberType,
                }),
                isVisitorToMemberDisabled: isWriteBeforeLoginDisabled,
            });
        })(),
        REACT: (() => {
            if (permissions.permissions.reactToComment) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            if (isLocked) {
                return { type: 'DENY', reason: 'LOCKED' };
            }
            return predictActionPermission({
                memberType,
                permissions,
                isConsumerPreconditionRequired: isPreconditionRequiredForAction === null || isPreconditionRequiredForAction === void 0 ? void 0 : isPreconditionRequiredForAction('REACT', {
                    srcResourceId,
                    memberType,
                }),
            });
        })(),
        VOTE: (() => {
            if (permissions.permissions.voteComment) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            if (isLocked) {
                return { type: 'DENY', reason: 'LOCKED' };
            }
            return predictActionPermission({
                memberType,
                permissions,
                isConsumerPreconditionRequired: isPreconditionRequiredForAction === null || isPreconditionRequiredForAction === void 0 ? void 0 : isPreconditionRequiredForAction('VOTE', {
                    srcResourceId,
                    memberType,
                }),
            });
        })(),
        RATE: (() => {
            if (isLocked) {
                return { type: 'DENY', reason: 'LOCKED' };
            }
            if (permissions.permissions.rateResource) {
                return {
                    type: 'ALLOW',
                    precondition: memberType === 'VISITOR' ? 'GUEST_FLOW' : 'NONE',
                };
            }
            return predictActionPermission({
                memberType,
                permissions,
                isConsumerPreconditionRequired: isPreconditionRequiredForAction === null || isPreconditionRequiredForAction === void 0 ? void 0 : isPreconditionRequiredForAction('RATE', {
                    srcResourceId,
                    memberType,
                }),
                isVisitorToMemberDisabled: isWriteBeforeLoginDisabled,
            });
        })(),
        MARK: (() => {
            if (permissions.permissions.markComment) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            return { type: 'DENY', reason: 'UNKNOWN' };
        })(),
        REPORT: (() => {
            if (permissions.permissions.reportComment) {
                return { type: 'ALLOW', precondition: 'NONE' };
            }
            return { type: 'DENY', reason: 'UNKNOWN' };
        })(),
    };
};
export const resolveMemberType = (currentOwner) => {
    var _a;
    switch (currentOwner.type) {
        case 'UNKNOWN':
            return 'UNKNOWN';
        case 'ANONYMOUS':
            return 'VISITOR';
        case 'MEMBER':
            return (_a = currentOwner.member.privacyStatus) !== null && _a !== void 0 ? _a : 'PRIVATE';
        default:
            throw unreachable(currentOwner);
    }
};
// We don't know know if the user will have ability to do action if he will login,
// so we try to make best guese here
const predictActionPermission = ({ memberType, permissions, isConsumerPreconditionRequired, isVisitorToMemberDisabled, }) => {
    switch (memberType) {
        case 'PUBLIC':
        case 'PRIVATE': {
            if (isConsumerPreconditionRequired) {
                return { type: 'ALLOW', precondition: 'CONSUMER_PRECONDITION' };
            }
            return { type: 'DENY', reason: 'UNKNOWN' };
        }
        // 'UNKNOWN' should happen only in SSR when visitorId is not passed, so we fallback to visitor to avoid permission switch
        case 'UNKNOWN':
        case 'VISITOR': {
            if (isVisitorToMemberDisabled) {
                return { type: 'DENY', reason: 'LOGIN_REQUIRED' };
            }
            return permissions.registrationData.registrationOpened &&
                !permissions.registrationData.registrationManuallyApproved &&
                !permissions.registrationData.requireEmailVerification
                ? { type: 'ALLOW', precondition: 'VISITOR_TO_MEMBER' }
                : { type: 'DENY', reason: 'LOGIN_REQUIRED' };
        }
        default:
            throw unreachable(memberType);
    }
};
export const isWithGuestFlow = (permissionState) => {
    return permissionState.type === 'ALLOW' && permissionState.precondition === 'GUEST_FLOW';
};
//# sourceMappingURL=comments-permissions.js.map